<template>
  <div class="bids-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :search="tableData.search"
                   :hide-per-page-selector="hidePerPageSelector"
                   :enable-simple-search="false" :export-vuex-module-name="hideExport ? null: 'bids'" :index="false"
                   :export-attributes="exportAttributes" export-permission-name="export_auction"
                   @paramsChanged="getTableData" filter-toggle-string="bids-table" :extra-data="extraData"
                   enable-frontend-export @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns" show-toggle-columns>

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown v-if="!enableContinuousAuction" :text="$t('Actions')" variant="primary">
          <b-dropdown-item @click="onNewBidClick" :disabled="isCreateBidDisabled">
            {{ $t('New Bid') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="bids-table">
          <bid-search v-if="!hideSearch" :update-search-model="updateSearchModel" :get-table-data="getTableData"
                      :vuex-search-model="tableData.searchModel" :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template slot="cell(bidder)" slot-scope="{item}">
        <router-link v-if="item.bidder.customer && $hasPermission('view_customer')"
                     :to="{ name: 'customers.dashboard', params: { id: item.bidder.customer_uuid }}">
          {{ item.bidder.customer.display_name }}
        </router-link>
      </template>
      <template slot="cell(amount)" slot-scope="{item}">
        {{ item.amount | asCurrency(getCurrency(item)) }}
      </template>
      <template slot="cell(title)" slot-scope="{item}">
        {{ item.lot.inventory.short_description | striptags | truncate(50) }}
      </template>
      <template slot="cell(sale_no)" slot-scope="{item}">
        <span v-if="item.lot && item.lot.auction">
          {{ item.lot.auction.sale_no }} - {{ item.lot.auction.title | masterLanguage | striptags | truncate(50) }}
        </span>
      </template>
      <template slot="cell(lot_no)" slot-scope="{item}">
        {{ item.lot.lot_no }}
      </template>
      <template slot="cell(stock_no)" slot-scope="{item}">
        {{ item.lot.inventory.stock_no }}
      </template>
      <template slot="cell(date)" slot-scope="{item}">
        {{ item.date | toDate }} {{ item.date | toDate('HH:mm:ss') }}
      </template>
      <template slot="cell(is_winner)" slot-scope="{item}">
        <span class="badge bg-success text-white" v-if="item.is_winner">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>
      <template slot="cell(email)" slot-scope="{item}">
        <template v-if="item.bidder && item.bidder.customer && item.bidder.customer.email">
          <span class="badge border mr-1">
            <a :href="`mailto:` + item.bidder.customer.email"> {{ item.bidder.customer.email }}</a>
          </span>
        </template>
      </template>
      <template slot="cell(phone)" slot-scope="{item}">
        <template v-if="item.bidder && item.bidder.customer && item.bidder.customer.phone">
          <span class="badge border mr-1" v-for="(phone, index) in item.bidder.customer.phone.split(',')"
                :key="`grid-phone-${index}`">
            {{ phone }}
          </span>
        </template>
      </template>
      <template slot="cell(actions)" slot-scope="{item}">
        <b-tooltip :target="`mark-as-winner-btn-${item.uuid}`" :title="$t('Mark As Winner')"/>
        <b-button v-if="!item.is_winner" variant="primary" size="sm" :id="`mark-as-winner-btn-${item.uuid}`"
                  @click="markBidAsWinner(item)" v-permission="'auction_results_auction'">
          <font-awesome-icon icon="check" class="icon"/>
        </b-button>

        <template v-if="!item.lot.is_finished">
          <b-tooltip :target="`delete-bid-btn-wrapper-${item.uuid}`"/>
          <div
            class="d-inline-block tooltip-wrapper"
            :id="`delete-bid-btn-wrapper-${item.uuid}`"
          >
            <b-button class="ml-2" v-if="item.is_latest" variant="danger" size="sm"
                      :id="`delete-bid-btn-${item.uuid}`"
                      @click="deleteCurrentWinningBid(item)">
              <font-awesome-icon icon="trash" class="icon"/>
            </b-button>
          </div>
        </template>
      </template>
      <template slot="cell(type)" slot-scope="{item: sale}">
        <span v-as-status="sale.type">
          {{ sale.type | toUpperCaseFirst | removeUnderscores | translate}}
        </span>
      </template>
      <template slot="cell(created_data)" slot-scope="{item: bid}">
        {{ bid.created_at | toDate }} {{ bid.created_at | toDate('HH:mm:ss') }}
        by {{ bid.created_by.display_name }}
      </template>
    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import i18n from "../../shared/i18n";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import BidSearch from "@/modules/bids/BidSearch";
  import {
    BIDS_TABLE_NAME,
    SOURCE_BIDS_TABLE,
  } from "@/modules/constants";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";
  import cloneDeep from "lodash/cloneDeep";

  const {mapActions, mapState} = createNamespacedHelpers('bids');
  const {mapState: salesMapState} = createNamespacedHelpers('sales');
  const {mapState: mapGlobalState} = createNamespacedHelpers('global');

  export default {
    name: "BidsTable",
    components: {BidSearch, SearchCollapse, ArtisioTable},
    props: {
      excludeColumns: {
        type: Array,
        default: () => [],
      },
      excludeSearchFields: {
        type: Array,
        default: () => [],
      },
      hideSearch: {
        type: Boolean,
        default: false
      },
      hideExport: {
        type: Boolean,
        default: false
      },
      hidePerPageSelector: {
        type: Boolean,
        default: false
      },
      hideBidCreation: {
        type: Boolean,
        default: false
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        exportAttributes: {
          'bidder.customer.display_name': i18n.t('Bidder'),
          'bidder.customer.email': i18n.t('Email'),
          'lot.lot_no': i18n.t('Lot'),
          'lot.inventory.short_description': i18n.t('Title'),
          amount: i18n.t('Amount'),
          is_winner: i18n.t('Winner'),
          date: i18n.t('Date'),
        }
      }
    },
    computed: {
      ...mapState({
        tableSource: state => state.tableSource,
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
      }),
      ...salesMapState({
        sale: state => state.view.currentSale
      }),
      ...mapGlobalState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
      }),
      isCreateBidDisabled() {
        if (this.enableContinuousAuction || this.hideBidCreation) {
          return;
        }
        const isTimed = this.sale.type === 'timed';
        const isPublished = this.sale.status === 'published';
        return !(isTimed && isPublished && this.hasArtisioTimedProvider);
      },
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f => f.isVisible);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.excludeColumns,
          tableName: `${BIDS_TABLE_NAME}.${this.tableSource}`,
        }
      }
    },
    methods: {
      ...mapActions([
        'getTableData',
        'updateSearchModel',
        'updateWinnerBid',
        'deleteLatestBid',
        'showModal',
        'updateToggleColumns',
      ]),
      onNewBidClick() {
        this.showModal({
          object: null,
          sale: cloneDeep(this.sale),
          source: SOURCE_BIDS_TABLE
        });
      },
      async markBidAsWinner(bid) {

        const reservation_proposals = (await LotsService.getLatestReservationProposals({bid_uuids: [bid.uuid]})).body
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) return
        }

        let initialResponse = await this.updateWinnerBid({uuid: bid.uuid});

        if (initialResponse.success) {
          this.$toast('success', this.$i18n.t('Bid was successfully marked as winner'));
          return;
        }
        if (initialResponse.body.confirmation) {
          let success = await this.$confirm(initialResponse.body.confirmation);
          if (success) {
            let secondResponse = await this.updateWinnerBid({uuid: bid.uuid, force_send: true});
            if (secondResponse.success) {
              this.$toast('success', this.$i18n.t("Bid was successfully marked as winner"));
            } else {
              if (secondResponse.toastError) {
                this.$errorDialog(secondResponse.toastError);
              } else {
                if (secondResponse && secondResponse.body.non_field_errors) {
                  this.$errorDialog(secondResponse.body.non_field_errors[0]);
                }
              }
            }
          }
        }
      },
      async deleteCurrentWinningBid(bid) {
        let success = await this.$confirm(this.$i18n.t('Are you sure you want delete the following bid?'))
        if (!success) return;

        let response = await this.deleteLatestBid({uuid: bid.uuid});

        if (response.success) {
          this.$toast('success', this.$i18n.t('Bid was successfully deleted'));
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0])
        }
      },
      getCurrency(item) {
        if (!this.enableContinuousAuction && this.tableData.sale) {
          return this.tableData.sale.currency.symbol;
        } else if (item.bidder && item.bidder.auction && item.bidder.auction.currency) {
          return item.bidder.auction.currency.symbol;
        }
        return null;
      },
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";
</style>

